import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Search, Users, Shield, ShieldOff, UserCheck, Building2, Home as HomeIcon,
  User, Phone, Calendar, ChevronLeft, ChevronRight, Eye, UserPlus, X, Clock,
  ExternalLink,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AssignRoleDialog, ROLE_BADGE_CONFIG } from './AssignRoleDialog';
import { InviteStaffDialog } from './InviteStaffDialog';

const PAGE_SIZE = 15;

function timeAgo(iso?: string) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

export function UserManagement() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [assignUser, setAssignUser] = useState<any>(null);
  const [confirmAction, setConfirmAction] = useState<{ userId: string; action: 'activate' | 'deactivate' } | null>(null);
  const [removeRole, setRemoveRole] = useState<{ userId: string; role: string; userName: string } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<'activate' | 'deactivate' | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;

      const { data: roles, error: rolesErr } = await supabase
        .from('user_roles')
        .select('*');
      if (rolesErr) throw rolesErr;

      const { data: members } = await supabase
        .from('internal_team_members')
        .select('user_id, team_id');
      const { data: teams } = await supabase
        .from('internal_teams')
        .select('id, department_id');
      const { data: depts } = await supabase
        .from('internal_departments')
        .select('id, name');

      // Join agent_profiles → agencies for the "managed by" chip on agent rows
      const { data: agentProfs } = await supabase
        .from('agent_profiles')
        .select('user_id, agency_id');
      const { data: agenciesData } = await supabase
        .from('agencies')
        .select('id, name, slug');
      const agencyById: Record<string, { id: string; name: string; slug: string | null }> = {};
      (agenciesData || []).forEach((a: any) => { agencyById[a.id] = a; });
      const userAgency: Record<string, { id: string; name: string; slug: string | null }> = {};
      (agentProfs || []).forEach((ap: any) => {
        if (ap.agency_id && agencyById[ap.agency_id]) userAgency[ap.user_id] = agencyById[ap.agency_id];
      });

      const teamToDept: Record<string, string> = {};
      (teams || []).forEach((t: any) => { teamToDept[t.id] = t.department_id; });
      const deptName: Record<string, string> = {};
      (depts || []).forEach((d: any) => { deptName[d.id] = d.name; });
      const userDept: Record<string, string> = {};
      (members || []).forEach((m: any) => {
        const dn = deptName[teamToDept[m.team_id]];
        if (dn && !userDept[m.user_id]) userDept[m.user_id] = dn;
      });

      const roleMap: Record<string, string[]> = {};
      (roles || []).forEach((r: any) => {
        if (!roleMap[r.user_id]) roleMap[r.user_id] = [];
        roleMap[r.user_id].push(r.role);
      });

      return (profiles || []).map((p: any) => ({
        ...p,
        roles: roleMap[p.user_id] || ['user'],
        primaryRole: (roleMap[p.user_id] || ['user']).find((r: string) => r !== 'user') || 'user',
        department: userDept[p.user_id] || null,
        agency: userAgency[p.user_id] || null,
      }));
    },
  });

  const departmentOptions = useMemo(() => {
    const set = new Set<string>();
    (data || []).forEach((u: any) => { if (u.department) set.add(u.department); });
    return Array.from(set).sort();
  }, [data]);

  const superadminCount = useMemo(
    () => (data || []).filter((u: any) => u.roles.includes('superadmin')).length,
    [data]
  );

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ userId, isActive }: { userId: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('profiles')
        .update({ is_active: isActive } as any)
        .eq('user_id', userId);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success(vars.isActive ? 'User account activated.' : 'User account deactivated.');
      setConfirmAction(null);
    },
    onError: (err: any) => toast.error(err.message),
  });

  const removeRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId)
        .eq('role', role as any);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('Role removed.');
      setRemoveRole(null);
    },
    onError: (err: any) => toast.error(err.message),
  });

  const bulkToggleMutation = useMutation({
    mutationFn: async ({ ids, isActive }: { ids: string[]; isActive: boolean }) => {
      const { error } = await supabase
        .from('profiles')
        .update({ is_active: isActive } as any)
        .in('user_id', ids);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success(`${vars.ids.length} user${vars.ids.length === 1 ? '' : 's'} ${vars.isActive ? 'activated' : 'deactivated'}.`);
      setSelected(new Set());
      setBulkAction(null);
    },
    onError: (err: any) => toast.error(err.message),
  });

  const filtered = (data || []).filter((u: any) => {
    const matchesSearch = !searchTerm ||
      u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.phone?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || u.roles.includes(roleFilter);
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && u.is_active !== false) ||
      (statusFilter === 'inactive' && u.is_active === false);
    const matchesDept = deptFilter === 'all' ||
      (deptFilter === '__none' ? !u.department : u.department === deptFilter);
    return matchesSearch && matchesRole && matchesStatus && matchesDept;
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const allOnPageSelected = paginated.length > 0 && paginated.every((u: any) => selected.has(u.user_id));
  const togglePageSelection = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        paginated.forEach((u: any) => next.delete(u.user_id));
      } else {
        paginated.forEach((u: any) => { if (!u.roles.includes('superadmin')) next.add(u.user_id); });
      }
      return next;
    });
  };
  const toggleOne = (userId: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const handleRemoveRole = (u: any, role: string) => {
    if (role === 'user') {
      toast.error('Base "user" role cannot be removed.');
      return;
    }
    if (role === 'agent') {
      toast.error('Agents are managed by their agency. Please ask the Agency Admin to remove this member.');
      return;
    }
    if (role === 'superadmin' && superadminCount <= 1) {
      toast.error('Cannot remove the last Super Admin.');
      return;
    }
    setRemoveRole({ userId: u.user_id, role, userName: u.full_name || 'this user' });
  };

  const stats = {
    total: data?.length || 0,
    agents: data?.filter((u: any) => u.roles.includes('agent')).length || 0,
    developers: data?.filter((u: any) => u.roles.includes('developer')).length || 0,
    agencies: data?.filter((u: any) => u.roles.includes('agency_admin')).length || 0,
    inactive: data?.filter((u: any) => u.is_active === false).length || 0,
  };

  if (isLoading) {
    return <div className="space-y-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-bold">User Management</h2>
          <p className="text-sm text-muted-foreground mt-1">View all registered users, roles, and manage account status.</p>
        </div>
        <Button onClick={() => setInviteOpen(true)} className="gap-2">
          <UserPlus className="h-4 w-4" /> Invite Staff
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Total Users', value: stats.total, icon: Users, color: 'text-primary' },
          { label: 'Agents', value: stats.agents, icon: UserCheck, color: 'text-blue-600' },
          { label: 'Agencies', value: stats.agencies, icon: Building2, color: 'text-purple-600' },
          { label: 'Developers', value: stats.developers, icon: HomeIcon, color: 'text-emerald-600' },
          { label: 'Deactivated', value: stats.inactive, icon: ShieldOff, color: 'text-red-600' },
        ].map(s => (
          <Card key={s.label} className="rounded-xl border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <s.icon className={`h-5 w-5 ${s.color}`} />
              <div>
                <div className="text-lg font-bold">{s.value}</div>
                <div className="text-[10px] text-muted-foreground">{s.label}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or phone..."
            value={searchTerm}
            onChange={e => { setSearchTerm(e.target.value); setPage(1); }}
            className="pl-9 h-9"
          />
        </div>
        <Select value={roleFilter} onValueChange={v => { setRoleFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[180px] h-9">
            <SelectValue placeholder="All Roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="agency_admin">Agency Admin</SelectItem>
            <SelectItem value="developer">Developer</SelectItem>
            <SelectItem value="superadmin">Super Admin</SelectItem>
            <SelectItem value="sales_agent">Sales Agent</SelectItem>
            <SelectItem value="sales_team_leader">Sales Team Leader</SelectItem>
            <SelectItem value="sales_manager">Sales Manager</SelectItem>
            <SelectItem value="marketing_agent">Marketing Agent</SelectItem>
            <SelectItem value="marketing_team_leader">Marketing TL</SelectItem>
            <SelectItem value="marketing_manager">Marketing Manager</SelectItem>
            <SelectItem value="it_staff">IT Staff</SelectItem>
            <SelectItem value="accounting_staff">Accounting</SelectItem>
            <SelectItem value="hr_staff">HR Staff</SelectItem>
          </SelectContent>
        </Select>
        <Select value={deptFilter} onValueChange={v => { setDeptFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[180px] h-9">
            <SelectValue placeholder="All Departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            <SelectItem value="__none">No Department</SelectItem>
            {departmentOptions.map(d => (
              <SelectItem key={d} value={d}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[130px] h-9">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Deactivated</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-muted-foreground ml-auto">{filtered.length} results</div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
          <div className="text-sm font-medium">{selected.size} selected</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set())}>Clear</Button>
            <Button size="sm" variant="outline" className="text-green-700" onClick={() => setBulkAction('activate')}>
              <Shield className="h-3.5 w-3.5 mr-1" /> Activate
            </Button>
            <Button size="sm" variant="outline" className="text-red-700" onClick={() => setBulkAction('deactivate')}>
              <ShieldOff className="h-3.5 w-3.5 mr-1" /> Deactivate
            </Button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <Card className="rounded-xl border-0 shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]">
                <Checkbox checked={allOnPageSelected} onCheckedChange={togglePageSelection} aria-label="Select all on page" />
              </TableHead>
              <TableHead>User</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Roles</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">No users found</TableCell>
              </TableRow>
            ) : paginated.map((u: any) => {
              const isSuper = u.roles.includes('superadmin');
              const isAgent = u.roles.includes('agent') && !u.roles.includes('agency_admin');
              return (
                <TableRow key={u.id} className={u.is_active === false ? 'opacity-60' : ''}>
                  <TableCell>
                    <Checkbox
                      checked={selected.has(u.user_id)}
                      onCheckedChange={() => toggleOne(u.user_id)}
                      disabled={isSuper}
                      aria-label="Select user"
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        {u.avatar_url ? (
                          <img src={u.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover" />
                        ) : (
                          <User className="h-4 w-4 text-primary" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-sm">{u.full_name || 'No Name'}</div>
                        {u.bio && <div className="text-xs text-muted-foreground line-clamp-1 max-w-[200px]">{u.bio}</div>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{u.phone || '—'}</TableCell>
                  <TableCell className="text-sm">
                    {u.department ? (
                      <Badge variant="outline" className="text-[10px] font-normal">{u.department}</Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 items-center">
                      {u.roles.filter((r: string) => r !== 'user').map((r: string) => (
                        <Badge
                          key={r}
                          className={`text-[10px] gap-1 pr-1 ${ROLE_BADGE_CONFIG[r]?.color || ''}`}
                        >
                          {ROLE_BADGE_CONFIG[r]?.label || r}
                          {r !== 'agent' && (
                            <button
                              onClick={() => handleRemoveRole(u, r)}
                              className="hover:bg-black/10 rounded-full p-0.5 transition-colors"
                              title="Remove role"
                            >
                              <X className="h-2.5 w-2.5" />
                            </button>
                          )}
                        </Badge>
                      ))}
                      {u.agency && u.roles.includes('agent') && (
                        <Badge variant="outline" className="text-[10px] font-normal gap-1 border-primary/30 text-primary">
                          <Building2 className="h-2.5 w-2.5" />
                          {u.agency.name}
                        </Badge>
                      )}
                      {u.roles.filter((r: string) => r !== 'user').length === 0 && (
                        <Badge variant="outline" className="text-[10px]">User</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={u.is_active === false ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}>
                      {u.is_active === false ? 'Deactivated' : 'Active'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {timeAgo(u.updated_at)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setSelectedUser(u)} title="View Details">
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      {isAgent ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-primary hover:text-primary"
                          onClick={() => navigate(u.agency?.slug ? `/agencies/${u.agency.slug}` : '/agencies')}
                          title="Agents are managed by their agency. Open agency profile."
                        >
                          <ExternalLink className="h-3.5 w-3.5 mr-1" />
                          <span className="text-[10px]">Manage via Agency</span>
                        </Button>
                      ) : (
                        <>
                          <Button variant="ghost" size="sm" className="h-8 px-2 text-primary hover:text-primary" onClick={() => setAssignUser(u)} title="Manage Roles">
                            <UserPlus className="h-3.5 w-3.5" />
                          </Button>
                          {!isSuper && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className={`h-8 px-2 ${u.is_active === false ? 'text-green-600 hover:text-green-700' : 'text-red-600 hover:text-red-700'}`}
                              onClick={() => setConfirmAction({
                                userId: u.user_id,
                                action: u.is_active === false ? 'activate' : 'deactivate',
                              })}
                              title={u.is_active === false ? 'Activate' : 'Deactivate'}
                            >
                              {u.is_active === false ? <Shield className="h-3.5 w-3.5" /> : <ShieldOff className="h-3.5 w-3.5" />}
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </div>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* User Detail Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" /> User Details
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                  {selectedUser.avatar_url ? (
                    <img src={selectedUser.avatar_url} alt="" className="h-16 w-16 rounded-full object-cover" />
                  ) : (
                    <User className="h-8 w-8 text-primary" />
                  )}
                </div>
                <div>
                  <h3 className="font-bold">{selectedUser.full_name || 'No Name'}</h3>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedUser.roles.map((r: string) => (
                      <Badge key={r} className={`text-[10px] ${ROLE_BADGE_CONFIG[r]?.color || ''}`}>
                        {ROLE_BADGE_CONFIG[r]?.label || r}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                {selectedUser.phone && (
                  <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /> {selectedUser.phone}</div>
                )}
                {selectedUser.department && (
                  <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" /> {selectedUser.department}</div>
                )}
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" /> Joined {new Date(selectedUser.created_at).toLocaleDateString()}
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" /> Last active {timeAgo(selectedUser.updated_at)}
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <Badge className={selectedUser.is_active === false ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}>
                    {selectedUser.is_active === false ? 'Deactivated' : 'Active'}
                  </Badge>
                </div>
                {selectedUser.bio && (
                  <div className="pt-2">
                    <div className="text-xs text-muted-foreground font-medium mb-1">Bio</div>
                    <p className="text-sm bg-muted/50 p-3 rounded-lg">{selectedUser.bio}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Role Dialog */}
      <AssignRoleDialog
        open={!!assignUser}
        onOpenChange={() => setAssignUser(null)}
        user={assignUser}
      />

      {/* Invite Staff Dialog */}
      <InviteStaffDialog open={inviteOpen} onOpenChange={setInviteOpen} />

      {/* Confirm Activate/Deactivate */}
      <Dialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{confirmAction?.action === 'deactivate' ? 'Deactivate Account' : 'Activate Account'}</DialogTitle>
            <DialogDescription>
              {confirmAction?.action === 'deactivate'
                ? 'This user will no longer be able to access the platform. You can reactivate them later.'
                : 'This user will regain access to the platform.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmAction(null)}>Cancel</Button>
            <Button
              variant={confirmAction?.action === 'deactivate' ? 'destructive' : 'default'}
              onClick={() => {
                if (confirmAction) {
                  toggleActiveMutation.mutate({
                    userId: confirmAction.userId,
                    isActive: confirmAction.action === 'activate',
                  });
                }
              }}
              disabled={toggleActiveMutation.isPending}
            >
              {confirmAction?.action === 'deactivate' ? 'Deactivate' : 'Activate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Remove Role */}
      <Dialog open={!!removeRole} onOpenChange={() => setRemoveRole(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove Role</DialogTitle>
            <DialogDescription>
              Remove the <strong>{ROLE_BADGE_CONFIG[removeRole?.role || '']?.label || removeRole?.role}</strong> role from <strong>{removeRole?.userName}</strong>? They will lose all permissions tied to this role.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRemoveRole(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => removeRole && removeRoleMutation.mutate({ userId: removeRole.userId, role: removeRole.role })}
              disabled={removeRoleMutation.isPending}
            >
              Remove Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Bulk Action */}
      <Dialog open={!!bulkAction} onOpenChange={() => setBulkAction(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{bulkAction === 'deactivate' ? 'Deactivate Users' : 'Activate Users'}</DialogTitle>
            <DialogDescription>
              {bulkAction === 'deactivate'
                ? `Deactivate ${selected.size} user${selected.size === 1 ? '' : 's'}? They will lose access until reactivated.`
                : `Activate ${selected.size} user${selected.size === 1 ? '' : 's'}? They will regain access immediately.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBulkAction(null)}>Cancel</Button>
            <Button
              variant={bulkAction === 'deactivate' ? 'destructive' : 'default'}
              onClick={() => bulkAction && bulkToggleMutation.mutate({ ids: Array.from(selected), isActive: bulkAction === 'activate' })}
              disabled={bulkToggleMutation.isPending}
            >
              {bulkAction === 'deactivate' ? 'Deactivate' : 'Activate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
